import {FetchResult} from '@apollo/client';
import {builderFieldsType} from '../CreateBuilder/builderFields';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import {OvbuilderDetails_ovrecommendDetails} from '../../../schema-types/OvbuilderDetails';
import {BuilderPot} from '../../../schema-types/BuilderPot';
import {
  UpsertOvbuilder,
  UpsertOvbuilderVariables,
} from '../../../schema-types/UpsertOvbuilder';
import validateUUID from '../../../utils/validateUUID';
import {
  UpsertSetUpNew,
  UpsertSetUpNewVariables,
} from '../../../schema-types/UpsertSetUpNew';
import {
  UpsertBuilderModify,
  UpsertBuilderModifyVariables,
} from '../../../schema-types/UpsertBuilderModify';
import {
  UpsertBuilderProtection,
  UpsertBuilderProtectionVariables,
} from '../../../schema-types/UpsertBuilderProtection';
import {
  UpsertBuilderAnnuity,
  UpsertBuilderAnnuityVariables,
} from '../../../schema-types/UpsertBuilderAnnuity';
import {
  UpsertBuilderContribute,
  UpsertBuilderContributeVariables,
} from '../../../schema-types/UpsertBuilderContribute';
import {
  UpsertBuilderWithdraw,
  UpsertBuilderWithdrawVariables,
} from '../../../schema-types/UpsertBuilderWithdraw';
import {
  UpsertBuilderTransfer,
  UpsertBuilderTransferVariables,
} from '../../../schema-types/UpsertBuilderTransfer';
import {
  UpsertBuilderAction,
  UpsertBuilderActionVariables,
} from '../../../schema-types/UpsertBuilderAction';
import {
  UpsertBuilderChargeAdHocFee,
  UpsertBuilderChargeAdHocFeeVariables,
} from '../../../schema-types/UpsertBuilderChargeAdHocFee';
import {SaltusAny} from '../../../utils/SaltusAny';
import {toUUID} from '../../../graphql/uuid';
import {AccountBasicFragment_contacts} from '../../../schema-types/AccountBasicFragment';
import {getRecommendationName} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {SPP_BUILDER_TYPES, builderFormToServerType} from '../constants/SolutionType';
import {cleanForCuro} from '../../../utils/cleanString';
import saltus from '../../../shared/logger';
import OWNERSHIP from '../../../constants/Ownership';
import {ComboFrequencyEnum} from '../../../schema-types/globalTypes';

// ============================================
// COMPUTED FIELD HELPERS (not stored in state)
// ============================================

export const getIsMinor = (
  contact: string | undefined,
  contacts: AccountBasicFragment_contacts[]
): boolean => {
  if (!contact) {
    return false;
  }
  const foundContact = contacts.find((c) => c.id === contact);
  return Boolean(foundContact?.isMinor);
};

export const getAdviceWhat = (planAction: string | undefined): string => {
  const planActionsMap: Record<string, string> = {
    contribute: 'Contribute to',
    withdraw: 'Withdraw from',
    transfer: 'Transfer to',
    setUpNew: 'Set up a new',
    modify: 'Modify a',
    adHocFee: 'Charge an Ad-hoc Fee to',
    action: 'Action',
  };
  return planActionsMap[planAction || ''] || '';
};

export const getAdvicePlan = (
  mainPlanType: string | undefined,
  planTypes: Array<{value: string; label: string; capitalise?: boolean}>
): string => {
  if (!mainPlanType) {
    return '';
  }
  const plan = planTypes.find((p) => p.value === mainPlanType);
  if (!plan) {
    return '';
  }
  return plan.capitalise ? plan.label : plan.label.toLowerCase();
};

export interface updateRecsVarsProps {
  accountId: string;
  values: builderFieldsType;
  builderDetails: OvbuilderDetails_ovrecommendDetails | null;
  ovrecommendId?: string;
  draft: boolean;
  upsertBuilder: (x: {
    variables: UpsertOvbuilderVariables;
  }) => Promise<FetchResult<UpsertOvbuilder>>;
  upsertSetUpNew: (x: {
    variables: UpsertSetUpNewVariables;
  }) => Promise<FetchResult<UpsertSetUpNew>>;
  upsertBuilderModify: (x: {
    variables: UpsertBuilderModifyVariables;
  }) => Promise<FetchResult<UpsertBuilderModify>>;
  upsertBuilderProtection: (x: {
    variables: UpsertBuilderProtectionVariables;
  }) => Promise<FetchResult<UpsertBuilderProtection>>;
  upsertBuilderAnnuity: (x: {
    variables: UpsertBuilderAnnuityVariables;
  }) => Promise<FetchResult<UpsertBuilderAnnuity>>;
  upsertBuilderContribute: (x: {
    variables: UpsertBuilderContributeVariables;
  }) => Promise<FetchResult<UpsertBuilderContribute>>;
  upsertBuilderWithdraw: (x: {
    variables: UpsertBuilderWithdrawVariables;
  }) => Promise<FetchResult<UpsertBuilderWithdraw>>;
  upsertBuilderTransfer: (x: {
    variables: UpsertBuilderTransferVariables;
  }) => Promise<FetchResult<UpsertBuilderTransfer>>;
  upsertBuilderAction: (x: {
    variables: UpsertBuilderActionVariables;
  }) => Promise<FetchResult<UpsertBuilderAction>>;
  upsertBuilderChargeAdHocFee: (x: {
    variables: UpsertBuilderChargeAdHocFeeVariables;
  }) => Promise<FetchResult<UpsertBuilderChargeAdHocFee>>;
  enqueueSnackbar: (m: string, o: SaltusAny) => void;
  // optional
  contactName?: string;
  sourcePots?: BuilderPot[];
  destinationPots?: BuilderPot[];
  reviewId: string;
  contacts?: AccountBasicFragment_contacts[];
}

export function nonEmptyStringOrNull<T>(val?: T): T | null {
  if (typeof val === 'string' && val !== '') {
    return val;
  }
  if (typeof val === 'boolean') {
    return val;
  }
  return null;
}

export const getPotContactId = (contact: string | undefined) => {
  if (contact === OWNERSHIP.TRUST) {
    return null;
  }
  if (contact === OWNERSHIP.JOINT_CODE) {
    return null;
  }
  return contact ? toUUID(contact) : null;
};

export const upsertBuilderHeader = async (
  props: updateRecsVarsProps,
  headerSpecificVars: Partial<UpsertOvbuilderVariables>
): Promise<string> => {
  const {
    ovrecommendId: existingOvBuilderId,
    values,
    contactName,
    upsertBuilder,
    reviewId,
  } = props;

  const variables = {
    note: cleanForCuro(values?.note),
    ...headerSpecificVars,
    ovrecommendId: validateUUID(existingOvBuilderId),
    solutionType: builderFormToServerType(values),
    reviewId: validateUUID(reviewId),
    name: getRecommendationName(values, contactName || ''),
    planType: values?.mainPlanType,
    isXo: values?.how === 'Instruction',
    isSppDaXo: values.sppType === SPP_BUILDER_TYPES.ExecutionOnly,
    isDependantsPension: values.transfer?.isDependantsPension,
    isAps: values.transfer?.isAps,
    isPensionSharing: values.transfer?.isPensionSharing,
  };

  Object.keys(variables).forEach((key) => {
    (variables as any)[key] = nonEmptyStringOrNull((variables as any)[key]);
  });

  const {data} = await upsertBuilder({variables: variables as UpsertOvbuilderVariables});

  if (data?.upsertOvrecommend?.ovrecommend) {
    const ovrecommendId = data.upsertOvrecommend.ovrecommend.id;
    saltus.log({recommends: data?.upsertOvrecommend?.ovrecommend.recommends});
    return ovrecommendId;
  }

  throw Error(`Could not create. Error: ${data?.upsertOvrecommend?.errors?.join('.')}`);
};

export const containsSingleFrequency = (comboFrequency: ComboFrequencyEnum) =>
  [
    ComboFrequencyEnum.SINGLE,
    ComboFrequencyEnum.ANNUAL_AND_SINGLE,
    ComboFrequencyEnum.MONTHLY_AND_SINGLE,
    ComboFrequencyEnum.QUARTERLY_AND_SINGLE,
  ].includes(comboFrequency);
